import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, ZoomIn, ZoomOut, RotateCcw, Award, ShieldCheck } from "lucide-react";

const CertificateViewer = ({ certificate, onClose }) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Prevent background scrolling while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus the close button on mount for accessibility
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Keyboard navigation: Escape to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "+" || e.key === "=") {
        setZoom((prev) => Math.min(prev + 0.25, 3));
      } else if (e.key === "-") {
        setZoom((prev) => {
          const next = Math.max(prev - 0.25, 0.75);
          if (next <= 1) setPan({ x: 0, y: 0 });
          return next;
        });
      } else if (e.key === "0") {
        setZoom(1);
        setPan({ x: 0, y: 0 });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Zoom handlers
  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => {
      const next = Math.max(prev - 0.25, 0.75);
      if (next <= 1) setPan({ x: 0, y: 0 });
      return next;
    });
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Mouse pan handlers when zoomed
  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: pan.x,
      panY: pan.y,
    };
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || zoom <= 1) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setPan({
      x: dragStartRef.current.panX + dx,
      y: dragStartRef.current.panY + dy,
    });
  }, [isDragging, zoom]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch pan handlers
  const handleTouchStart = (e) => {
    if (zoom <= 1 || e.touches.length !== 1) return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      panX: pan.x,
      panY: pan.y,
    };
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || zoom <= 1 || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - dragStartRef.current.x;
    const dy = e.touches[0].clientY - dragStartRef.current.y;
    setPan({
      x: dragStartRef.current.panX + dx,
      y: dragStartRef.current.panY + dy,
    });
  }, [isDragging, zoom]);

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  if (!certificate) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cert-viewer-title"
      ref={modalRef}
      onClick={(e) => {
        // Clicking backdrop closes the modal
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-[#040817]/90 backdrop-blur-md animate-fadeIn select-none"
    >
      <div
        className="relative flex flex-col w-full max-w-5xl h-[92vh] max-h-[880px] rounded-2xl overflow-hidden border border-white/10 shadow-[0_25px_80px_rgba(0,0,0,0.85)] bg-[#0B1226]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10 bg-[#060B1F]/90 z-20">
          <div className="flex items-center gap-3 min-w-0 pr-4">
            <span className="p-1.5 rounded-lg bg-[#7C4DFF]/15 border border-[#7C4DFF]/30 text-ai-glow">
              <ShieldCheck className="w-4 h-4 text-[#27B8FF]" />
            </span>
            <div className="min-w-0">
              <h2
                id="cert-viewer-title"
                className="text-white text-base sm:text-lg font-bold truncate leading-tight"
                style={{ fontFamily: '"Space Grotesk", sans-serif' }}
              >
                {certificate.title}
              </h2>
              <p className="text-xs text-[#27B8FF] truncate leading-tight mt-0.5">
                {certificate.issuer}
              </p>
            </div>
          </div>

          {/* Close Button */}
          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Close certificate viewer"
            className="flex-shrink-0 p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#27B8FF]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Certificate Viewport */}
        <div
          className="relative flex-1 w-full bg-slate-950/90 overflow-hidden flex items-center justify-center"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
          }}
        >
          {/* Subtle tech background */}
          <div className="absolute inset-0 tech-grid opacity-15 pointer-events-none" />

          {/* Zoomed & Panned Image */}
          <div
            className="relative flex items-center justify-center transition-transform duration-100 ease-out max-h-full max-w-full"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: "center center",
            }}
          >
            <img
              src={certificate.image}
              alt={certificate.title}
              className="max-h-[60vh] sm:max-h-[66vh] max-w-[85vw] sm:max-w-[75vw] object-contain rounded-lg shadow-2xl pointer-events-none"
              draggable={false}
            />
          </div>

          {/* Floating Zoom Controls Bar */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 rounded-full bg-slate-900/95 border border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.6)] backdrop-blur-md">
            <button
              onClick={handleZoomOut}
              disabled={zoom <= 0.75}
              aria-label="Zoom out"
              title="Zoom Out (-)"
              className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
              <ZoomOut className="w-4 h-4" />
            </button>

            <span className="text-xs font-mono font-medium text-slate-200 px-1 min-w-[44px] text-center">
              {Math.round(zoom * 100)}%
            </span>

            <button
              onClick={handleZoomIn}
              disabled={zoom >= 3}
              aria-label="Zoom in"
              title="Zoom In (+)"
              className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
              <ZoomIn className="w-4 h-4" />
            </button>

            <div className="w-[1px] h-4 bg-white/20 mx-1" />

            <button
              onClick={handleResetZoom}
              aria-label="Reset zoom"
              title="Reset Zoom (0)"
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Reset</span>
            </button>
          </div>
        </div>

        {/* Modal Footer Description */}
        <div className="px-5 py-3.5 bg-[#060B1F]/95 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 z-20">
          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed max-w-3xl">
            {certificate.description}
          </p>
          <span className="self-start sm:self-center inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#27B8FF] bg-[#27B8FF]/10 px-3 py-1 rounded-full border border-[#27B8FF]/20 flex-shrink-0">
            <Award className="w-3 h-3" />
            {certificate.badge}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CertificateViewer;

